// Voxel prefab types for strategy-game.
// Compatible with adventure-craft prefab data format.
// Each voxel represents a 1x1x1 box at unit scale; prefabs use 1/16 unit voxels.

/** World-units per prefab voxel. Single source — import this instead of redeclaring. */
export const VOXEL_SCALE = 0.0625; // 1/16 unit per voxel

export interface BlockVoxel {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly color: string;
  readonly type?: string;
  /**
   * Per-voxel PBR roughness in [0, 1]. Defaults to the material's 0.5 when
   * absent. Use low values (≤0.3) for polished metal / wet surfaces; high
   * values (≥0.8) for matte cloth / dirt.
   */
  readonly roughness?: number;
  /**
   * Per-voxel PBR metalness in [0, 1]. Defaults to the material's 0.1 when
   * absent. Set to 1.0 for pure metal (swords, armor); leave at 0 for
   * dielectric materials (cloth, wood, flesh).
   */
  readonly metalness?: number;
  /**
   * Per-voxel emissive color (`#RRGGBB`). Defaults to black (no emission).
   * Use for glowing runes, torch flames, magic crystals, creature eyes.
   * Emissive voxels still show their base `color` in addition to the glow.
   */
  readonly emissive?: string;
  /**
   * Team-coloured voxel (tabards, shield faces, banners). The renderer
   * replaces its hue with the owner's colour and uses `color`'s lightness as
   * the shade, so author team voxels in greys: light for lit faces, darker
   * for folds/trim. A prefab with no team voxels gets a light whole-body
   * faction tint instead.
   */
  readonly team?: boolean;
}

export interface CharacterPart {
  readonly name: string;
  readonly voxels: readonly BlockVoxel[];
  readonly scale?: readonly [number, number, number];
  readonly offset?: readonly [number, number, number];
  readonly rotation?: readonly [number, number, number];
}

/**
 * Per-species proportions for the high-fidelity unit path (unit-fidelity.md, stage v3). Data, not code: the
 * hi-fi builder reads it, lo-fi ignores it. 1 = as authored. Kept small so a unit stays within ~15% of its
 * lo-fi bounding size (units must fit the same tiles, rings and combat-step offsets).
 */
export interface HiFiTraits {
  /** Body width and depth multiplier around the unit's vertical axis (broad champions > 1, lither archers < 1). */
  readonly build?: number;
  /** Limb thickness multiplier (arms, legs, wings). */
  readonly limb?: number;
}

export interface CharacterPrefab {
  readonly name: string;
  readonly description?: string;
  readonly parts: readonly CharacterPart[];
  readonly maxHp: number;
  readonly canFly?: boolean;
  readonly faction?: string;
  readonly isBoss?: boolean;
  readonly skeletonType?: 'humanoid' | 'quadruped' | 'snake' | 'hexapod' | 'octapod';
  readonly behavior?: string;
  /** High-fidelity proportions (see HiFiTraits). */
  readonly hifi?: HiFiTraits;
}

export interface StructurePrefab {
  readonly name: string;
  readonly blocks: readonly BlockVoxel[];
}

export type PrefabCategory = 'character' | 'structure';

export interface RegisteredPrefab {
  readonly id: string;
  readonly name: string;
  readonly category: PrefabCategory;
  readonly prefab: CharacterPrefab | StructurePrefab;
}
